import React from 'react';
import PropTypes from 'prop-types';
import { HtmlSafe } from 'polaris-coreweb/exports';
import { withPolaris } from 'Components/protons/Polaris';
import { Icon } from 'Components/atoms/Icon';
import { Heading } from 'Components/molecules/Heading';
import './_style.scss';

/**
 * CheckListBlock component renders a list of items with check icons.
 *
 * @component
 * @example
 * const items = [
 *   { text: 'Item 1', iconType: 'check' },
 *   { text: 'Item 2', iconType: 'cross' }
 * ];
 * return (
 *   <CheckListBlock title="Example Title" items={items} />
 * );
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.context - Context object containing configuration.
 * @param {string} props.context.cssPrefix - CSS prefix for styling.
 * @param {string} props.title - Title of the check list.
 * @param {Array} props.items - Array of data objects for list items.
 * @param {string} props.items[].text - Text of the list item.
 * @param {string} props.items[].iconType - Icon type for the list item.
 * @returns {React.Element|null} The rendered component or null if no items are provided.
 */
export const CheckListBlock = ({
  context: {
    cssPrefix,
  },
  title,
  items,
}) => {
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <div className={`${cssPrefix}__check-list-block`}>
      {title && (
        <Heading size={2} extraClassNames={{ [`${cssPrefix}__check-list-block--title`]: true }}>
          {title}
        </Heading>
      )}

      <ul className={`${cssPrefix}__check-list-block--items`}>
        {items.map((item, index) => (
          <li key={index} className={`${cssPrefix}__check-list-block--item`}>
            <div className={`${cssPrefix}__check-list-block--item--icon`}>
              <Icon icon={`polaris__ico-${item?.iconType}`} />
            </div>
            <HtmlSafe html={item?.text} />
          </li>
        ))}
      </ul>
    </div>
  );
};

CheckListBlock.propTypes = {
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  title: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      iconType: PropTypes.string,
    }),
  ),
};

CheckListBlock.defaultProps = {
  context: { cssPrefix: 'polaris' },
  title: '',
  items: [],
};

export default withPolaris(CheckListBlock);
